import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import {
  Box,
  Button,
  FormControl
} from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import DropdownMenu from 'src/components/DropdownMenu/DropdownMenu';
import {
  selectClient,
  selectClientCreditCards
} from 'src/store/selectors/clientSelectors';
import {
  LOAD_CLIENT_BANK_ACCOUNTS_FOR_CONTRACT,
  LOAD_CLIENT_CREDIT_CARDS,
  loadClientBankAccountsForContract,
  loadClientCreditCards,
  loadClientBankAccountInformation,
  loadClientDetail
} from 'src/store/actions/clientActions';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import { ContractPaymentForm } from 'src/utils/constants/contracts';
import {
  getFirstBankAccountId,
  getFirstCreditCardId
} from 'src/utils/paymentMethodAutoSelection';
import {
  getPaymentFormOptions,
  getBankAccountOptions,
  getSecondaryBankAccountOptions,
  getCreditCardOptions,
  canSubmitPaymentMethod
} from 'src/utils/paymentMethodHelpers';
import NoBankAccountsAlert from 'src/views/contract/contracts/ContractForm/CurrentStep/StepSix/NoBankAccountsAlert';
import NoCreditCardsAlert from 'src/views/contract/contracts/ContractForm/CurrentStep/StepSix/NoCreditCardsAlert';
import NoClabeAlert from 'src/views/contract/contracts/ContractForm/CurrentStep/StepSix/NoClabeAlert';
import useStyles from '../styles';
import validationSchema from './schema';
import makeInitialState from './initialState';

const StepSix = ({ data, handleNext, handleBack, resetRef }) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const clientState = useSelector(selectClient);
  const bankAccountsRaw =
    clientState.detail?.contractPaymentMethod?.bankAccounts || [];
  const bankAccounts = Array.isArray(bankAccountsRaw) ? bankAccountsRaw : [];
  const creditCards = useSelector(selectClientCreditCards) || [];

  const validBankAccounts = useMemo(() => {
    return bankAccounts.filter(account => {
      return account.bankAccountNumber || account.accountNumber;
    });
  }, [bankAccounts]);


  const hasSantanderClabe = useMemo(() => {
    const santanderClabe = clientState.detail?.bankAccountDetail?.santanderClabe;
    return !!santanderClabe 
  }, [clientState.detail]);


  const isLoadingBankAccounts = useSelector(
    isLoadingSelector([LOAD_CLIENT_BANK_ACCOUNTS_FOR_CONTRACT.action])
  );
  const isLoadingCreditCards = useSelector(
    isLoadingSelector([LOAD_CLIENT_CREDIT_CARDS.action])
  );

  useEffect(() => {
    if (data.clientId) {
      dispatch(loadClientDetail(data.clientId));
      dispatch(loadClientBankAccountsForContract(data.clientId));
      dispatch(loadClientBankAccountInformation(data.clientId));
      dispatch(loadClientCreditCards(data.clientId));
    }
  }, [data.clientId, dispatch]);

  const initialState = useMemo(
    () => makeInitialState(data, validBankAccounts, creditCards),
    [data, validBankAccounts, creditCards]
  );

  const paymentFormOptions = useMemo(() => getPaymentFormOptions(), []);

  const bankAccountOptions = useMemo(
    () => getBankAccountOptions(validBankAccounts, t),
    [validBankAccounts, t]
  );

  const getSecondaryBankAccountOptionsForForm = useCallback(
    primaryBankAccountId => {
      return getSecondaryBankAccountOptions(
        validBankAccounts,
        primaryBankAccountId,
        t
      );
    },
    [validBankAccounts, t]
  );

  const creditCardOptions = useMemo(
    () => getCreditCardOptions(creditCards),
    [creditCards]
  );

  const onNext = useCallback(
    values => {
      const selectedBankAccount = values.bankAccountId
        ? validBankAccounts.find(acc => acc.id === values.bankAccountId)
        : null;
      const selectedSecondaryBankAccount = values.secondaryBankAccountId
        ? validBankAccounts.find(acc => acc.id === values.secondaryBankAccountId)
        : null;
      const selectedCard = values.cardId
        ? creditCards.find(card => card.id === values.cardId)
        : null;

      handleNext({
        paymentForm: values.paymentForm,
        bankAccountId: values.bankAccountId || null,
        secondaryBankAccountId: values.secondaryBankAccountId || null,
        cardId: values.cardId || null,
        bankAccount: selectedBankAccount,
        secondaryBankAccount: selectedSecondaryBankAccount,
        creditCard: selectedCard
      });
    },
    [handleNext, validBankAccounts, creditCards]
  );

  return (
    <Formik
      initialValues={initialState}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={onNext}
    >
      {({ handleSubmit, values, setFieldValue, resetForm }) => {
        resetRef.current = () => resetForm();

        const currentSecondaryBankAccountOptions = getSecondaryBankAccountOptionsForForm(
          values.bankAccountId
        );

        const canSubmit = canSubmitPaymentMethod({
          paymentForm: values.paymentForm,
          bankAccountId: values.bankAccountId,
          cardId: values.cardId,
          creditCardsCount: creditCards.length,
          hasSantanderClabe
        });

        return (
          <form onSubmit={handleSubmit}>
            <Box className={classes.container}>
              <NoBankAccountsAlert
                clientId={data.clientId}
                show={
                  values.paymentForm === ContractPaymentForm.AUTOMATIC_DEBIT &&
                  validBankAccounts.length === 0
                }
              />

              <NoCreditCardsAlert
                clientId={data.clientId}
                show={
                  values.paymentForm === ContractPaymentForm.CREDIT_CARD &&
                  creditCards.length === 0
                }
              />

              <NoClabeAlert
                show={
                  values.paymentForm === ContractPaymentForm.BANK_TRANSFER &&
                  !hasSantanderClabe
                }
              />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <DropdownMenu
                  list={paymentFormOptions}
                  value={values.paymentForm}
                  setValue={value => {
                    setFieldValue('paymentForm', value);
                    if (value === ContractPaymentForm.AUTOMATIC_DEBIT) {
                      setFieldValue('cardId', null);
                      setFieldValue(
                        'bankAccountId',
                        getFirstBankAccountId(validBankAccounts)
                      );
                      setFieldValue('secondaryBankAccountId', null);
                    } else if (value === ContractPaymentForm.CREDIT_CARD) {
                      setFieldValue('bankAccountId', null);
                      setFieldValue('secondaryBankAccountId', null);
                      setFieldValue(
                        'cardId',
                        getFirstCreditCardId(creditCards)
                      );
                    } else if (value === ContractPaymentForm.BANK_TRANSFER) {
                      setFieldValue('bankAccountId', null);
                      setFieldValue('secondaryBankAccountId', null);
                      setFieldValue('cardId', null);
                    }
                  }}
                  label={t('CONTRACT_VIEW.CONTRACT_DETAIL.PAYMENT_METHOD')}
                />
              </FormControl>

              {values.paymentForm === ContractPaymentForm.AUTOMATIC_DEBIT && (
                <>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <DropdownMenu
                      list={bankAccountOptions}
                      value={values.bankAccountId}
                      setValue={value => {
                        setFieldValue('bankAccountId', value);
                        if (value === values.secondaryBankAccountId) {
                          setFieldValue('secondaryBankAccountId', null);
                        }
                      }}
                      label={t('CONTRACT_VIEW.CONTRACT_DETAIL.BANK_ACCOUNT')}
                      disabled={
                        isLoadingBankAccounts || validBankAccounts.length === 0
                      }
                    />
                  </FormControl>
                  {currentSecondaryBankAccountOptions.length > 0 && (
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <DropdownMenu
                        list={currentSecondaryBankAccountOptions}
                        value={values.secondaryBankAccountId}
                        setValue={value =>
                          setFieldValue('secondaryBankAccountId', value)
                        }
                        label={t(
                          'CONTRACT_VIEW.CONTRACT_DETAIL.SECONDARY_BANK_ACCOUNT'
                        )}
                        disabled={
                          isLoadingBankAccounts ||
                          validBankAccounts.length < 2 ||
                          !values.bankAccountId
                        }
                      />
                    </FormControl>
                  )}
                </>
              )}

              {values.paymentForm === ContractPaymentForm.CREDIT_CARD &&
                creditCards.length > 0 && (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <DropdownMenu
                      list={creditCardOptions}
                      value={values.cardId}
                      setValue={value => setFieldValue('cardId', value)}
                      label={t('CONTRACT_VIEW.CONTRACT_DETAIL.CREDIT_CARD')}
                      isLoading={isLoadingCreditCards}
                    />
                  </FormControl>
                )}
            </Box>

            <Box
              className={classes.box}
              display="flex"
              justifyContent="space-between"
            >
              <Button
                className={classes.buttons}
                onClick={() => handleBack()}
                color="primary"
                variant="outlined"
              >
                {t('COMMON.BACK')}
              </Button>

              <Button
                className={clsx(classes.buttons, classes.buttonMain)}
                type="submit"
                color="primary"
                variant="outlined"
                disabled={!canSubmit}
              >
                {t('COMMON.NEXT')}
              </Button>
            </Box>
          </form>
        );
      }}
    </Formik>
  );
};

StepSix.propTypes = {
  data: PropTypes.object.isRequired,
  handleBack: PropTypes.func,
  handleNext: PropTypes.func,
  resetRef: PropTypes.object
};

export default StepSix;
